import { useT } from '@adiox/ui';
import { useMailbox } from '../mailbox/useMailbox';
import { useRef, useState } from 'react';
import { ServerClock, Wordmark, usePrefs, useSessionContext } from '@adiox/ui';
import { ContextMenu, type MenuItem } from '../chart/ChartOverlays';
import { useChallenge } from '../challenge/useChallenge';
import { useNotifications } from '../notifications/notificationStore';
import { useActiveLogin } from '../trading/useTrading';
import { AccountBar } from './AccountBar';
import { usePaymentsDialog } from '../payments/paymentsDialogStore';
import { ConnectionStatus } from './ConnectionStatus';
import { accountName, useAccountLabels } from './useAccountLabels';
import { useInstall } from './install';

/** A panel as the View menu lists it. Extra charts can only be closed: once closed they are gone. */
export interface PanelEntry {
  id: string;
  title: string;
  open: boolean;
  extra: boolean;
}

type Menu = { x: number; y: number; title: string; items: MenuItem[] } | null;

/**
 * The top bar: clock, connection and account figures, then the two things traders reach for all day (notifications and a
 * new order) and two menus: View (panels, charts, layout) and the user menu (account, settings, shortcuts, sign out).
 */
export function TopBar({
  onResetLayout,
  onOpenProfiles,
  onNewChart,
  onNewOrder,
  onShowNotifications,
  onShowMailbox,
  onOpenSettings,
  onOpenShortcuts,
  onOpenGuide,
  listPanels,
  onTogglePanel,
}: {
  onResetLayout: () => void;
  onOpenProfiles: () => void;
  onNewChart: () => void;
  onNewOrder: () => void;
  onShowNotifications: () => void;
  onShowMailbox: () => void;
  onOpenSettings: () => void;
  onOpenShortcuts: () => void;
  onOpenGuide: () => void;
  listPanels: () => PanelEntry[];
  onTogglePanel: (id: string) => void;
}) {
  const { tokens } = useSessionContext();
  const { density, setDensity } = usePrefs();
  const unread = useNotifications((s) => s.unread);
  const mail = useMailbox().unread;
  const t = useT();
  const challenge = useChallenge().data;
  const login = useActiveLogin();
  const labels = useAccountLabels();
  const install = useInstall();
  const viewButton = useRef<HTMLButtonElement>(null);
  const userButton = useRef<HTMLButtonElement>(null);
  const [menu, setMenu] = useState<Menu>(null);

  const below = (button: HTMLButtonElement | null, width = 0) => {
    const box = button!.getBoundingClientRect();
    return { x: Math.max(8, box.right - width), y: box.bottom + 4 };
  };

  const openView = () =>
    setMenu({
      ...below(viewButton.current, 220),
      title: t('View'),
      items: [
        ...listPanels()
          .filter((p) => p.id !== 'challenge' || challenge || p.open)
          .map<MenuItem>((p) =>
            p.extra
              ? { key: p.id, label: `Close ${p.title}`, onSelect: () => onTogglePanel(p.id) }
              : { key: p.id, label: p.title, checked: p.open, onSelect: () => onTogglePanel(p.id) },
          ),
        { key: 'new-chart', label: t('New chart'), onSelect: onNewChart },
        {
          key: 'density',
          label: t('Compact layout'),
          checked: density === 'compact',
          onSelect: () => setDensity(density === 'compact' ? 'comfortable' : 'compact'),
        },
        { key: 'profiles', label: t('Profiles…'), onSelect: onOpenProfiles },
        { key: 'reset', label: t('Reset layout'), danger: true, onSelect: onResetLayout },
      ],
    });

  const openUser = () =>
    setMenu({
      ...below(userButton.current, 240),
      title: login === undefined ? 'Account' : `Account ${accountName(login, labels.get(login))}`,
      items: [
        { label: t('Deposit and withdrawal…'), onSelect: () => usePaymentsDialog.getState().show() },
        { label: t('Settings'), onSelect: onOpenSettings },
        { label: t('Keyboard shortcuts'), shortcut: '?', onSelect: onOpenShortcuts },
        { label: t('Welcome guide'), onSelect: onOpenGuide },
        ...(install.canInstall
          ? [{ label: t('Install as an app'), onSelect: () => void install.install() }]
          : []),
        { label: t('Sign out'), danger: true, onSelect: () => void tokens.logout() },
      ],
    });

  const button =
    'whitespace-nowrap rounded-md border border-line bg-panel2 px-3 py-1 text-sm text-fg transition hover:border-accent focus-visible:border-accent';

  return (
    <header className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-panel px-4 py-2">
      <Wordmark className="h-7" />
      <div className="mx-1 h-5 w-px bg-line" aria-hidden="true" />
      <ServerClock />
      <ConnectionStatus />
      <div className="mx-1 h-5 w-px bg-line" aria-hidden="true" />
      <AccountBar />
      <div className="flex-1" />

      <button
        type="button"
        className={button}
        onClick={onShowMailbox}
        aria-label={mail > 0 ? `Mailbox, ${mail} unread` : 'Mailbox'}
        data-testid="mailbox-button"
      >
        {t('Mailbox')}
        {mail > 0 ? ` (${mail > 99 ? '99+' : mail})` : ''}
      </button>
      <button
        type="button"
        className={button}
        onClick={onShowNotifications}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
      >
        {t('Notifications')}
        {unread > 0 ? ` (${unread > 99 ? '99+' : unread})` : ''}
      </button>
      <button type="button" className={button} onClick={onNewOrder} title="New order (F9)">
        {t('New order')}
      </button>
      <button
        ref={viewButton}
        type="button"
        className={button}
        aria-haspopup="menu"
        aria-expanded={menu?.title === t('View')}
        aria-label="View"
        onClick={openView}
      >
        {t('View')} ▾
      </button>
      <button
        ref={userButton}
        type="button"
        className={button}
        aria-haspopup="menu"
        aria-expanded={menu !== null && menu.title !== t('View')}
        aria-label={`Account menu, signed in as account ${login ?? ''}`.trim()}
        onClick={openUser}
        data-testid="user-menu"
      >
        {login === undefined ? t('Account') : `#${login}`} ▾
      </button>
      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          title={menu.title}
          items={menu.items}
          onClose={() => {
            const opener = menu.title === t('View') ? viewButton : userButton;
            setMenu(null);
            opener.current?.focus();
          }}
        />
      )}
    </header>
  );
}
